/**
 * "Maak plaatje" (ADR-059): in the desktop preview of a package (`?pakket=`, the kit's `wereld bekijk`
 * of a folder), the view the maker walked to is drawn at this size and posted as a JPEG to the kit's
 * server, which keeps it as the world's picture. Pixel ratio 1, whatever the screen's.
 */
export const PREVIEW_PICTURE = { width: 1600, height: 900, quality: 0.9 } as const;

/**
 * Where to post the picture: `&plaatje=<path>` beside `?pakket=`, and only a path on this page's own
 * origin (like `?pakket=`): never another host, never a path a browser reads as `//other.host`.
 */
export function pictureAddress(
  search: string,
  origin: string = globalThis.location?.origin ?? 'http://localhost',
): string | undefined {
  const params = new URLSearchParams(search);
  const path = params.get('plaatje');
  if (!params.get('pakket') || !path) return undefined;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return undefined;
  let resolved: URL;
  try {
    resolved = new URL(path, `${origin}/`);
  } catch {
    return undefined;
  }
  return resolved.origin === origin ? resolved.href : undefined;
}

/** Posts the JPEG; what the server said (its `bericht`), or what went wrong, in Dutch. */
export async function sendPicture(
  url: string,
  jpeg: Blob,
  send: (url: string, init: RequestInit) => Promise<Response> = (u, i) => fetch(u, i),
): Promise<string> {
  let response: Response;
  try {
    response = await send(url, {
      method: 'POST',
      headers: { 'content-type': 'image/jpeg' },
      body: jpeg,
      cache: 'no-store',
    });
  } catch {
    return 'Het plaatje is niet bewaard: de weergave antwoordt niet (is wereld bekijk gestopt?).';
  }
  const said = await response
    .json()
    .then((body: unknown) =>
      typeof body === 'object' && body !== null && 'bericht' in body ? body.bericht : undefined,
    )
    .catch(() => undefined);
  if (typeof said === 'string' && said !== '') return said;
  return response.ok
    ? 'Plaatje bewaard als plaatje.jpg.'
    : `Het plaatje is niet bewaard (${response.status}).`;
}
